import { useEffect, useState } from "react";

/**
 * Navbar
 * ------
 * Transparent over the hero, turns into a frosted dark bar once the
 * user scrolls past ~40px. Sits fixed at the top so it stays visible
 * while the noise field animates behind it.
 */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { label: "Thesis", href: "#thesis" },
    { label: "Portfolio", href: "#portfolio" },
    { label: "Research", href: "#research" },
    { label: "Team", href: "#team" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        scrolled
          ? "backdrop-blur-md bg-black/60 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      style={{
        fontFamily:
          "'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif",
      }}
    >
      <div
        className={`mx-auto flex items-center justify-between px-6 md:px-10 transition-all duration-500 ${
          scrolled ? "h-14" : "h-20"
        }`}
      >
        {/* Logo */}
        <a
          href="#"
          className="group flex items-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <span className="relative w-7 h-7 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-fuchsia-500 blur-[6px] opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="relative w-3 h-3 rounded-full bg-white" />
          </span>
          <span className="text-white text-[15px] font-medium tracking-tight">
            Nova<span className="text-white/50">/Capital</span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="group relative px-4 py-2 text-[13.5px] text-white/70 hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              <span className="relative z-10">{item.label}</span>
              <span className="absolute left-4 right-4 bottom-1 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" />
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="#login"
            className="text-[13.5px] text-white/70 hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Sign in
          </a>
          <a
            href="#pitch"
            className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <span>Pitch us</span>
            <i className="ri-arrow-right-up-line text-[15px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-white/80 hover:text-white cursor-pointer"
        >
          <i
            className={`text-2xl transition-transform duration-300 ${
              menuOpen ? "ri-close-line rotate-90" : "ri-menu-line"
            }`}
          />
        </button>
      </div>

      {/* Mobile menu panel */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ease-out ${
          menuOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 pb-6 pt-2 flex flex-col gap-1 bg-black/70 backdrop-blur-md border-t border-white/10">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              className="py-3 text-[15px] text-white/80 hover:text-white border-b border-white/5 last:border-b-0 cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </a>
          ))}
          <div className="mt-4 flex flex-col gap-3">
            <a
              href="#login"
              className="text-center py-2.5 text-[14px] text-white/80 border border-white/15 rounded-full cursor-pointer whitespace-nowrap"
            >
              Sign in
            </a>
            <a
              href="#pitch"
              className="text-center py-2.5 text-[14px] text-black bg-white rounded-full font-medium cursor-pointer whitespace-nowrap"
            >
              Pitch us
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}